import { gsap } from 'gsap';
import { IEffect } from './IEffect';
import { Sprite, Vector2 } from '../../../odie';
import { getCollisionTracker } from '../../util/CollisionTracker';
import { config } from '../../defs/config';
import { GameScene } from '../../GameScene';
import { mapToViewPosition } from '../../util/mapTools';
import { Collider } from '../../defs/collide';

// types
//-----------------------------------------------------------------------------
export type RadialAttackEffectOptions = {
  position: Vector2;
  radius: number;
  duration: number;
  texture?: string;
  collider?: Collider;
  squareOff?: boolean;
};

/*
    radially attack blocks effect
*/
export class RadialAttackEffect implements IEffect {
  // fields
  //-------------------------------------------------------------------------
  // input
  private readonly _scene: GameScene;
  private readonly _options: RadialAttackEffectOptions;

  public targetCount = 0;

  // init
  //-------------------------------------------------------------------------
  constructor(scene: GameScene, options: RadialAttackEffectOptions) {
    this._scene = scene;
    this._options = options;
  }

  // impl
  //-------------------------------------------------------------------------
  public async execute() {
    const { position, radius, duration, collider } = this._options;
    const from = position.clone().subScalar(radius);
    const to = from.clone().addScalar(radius * 2 + 1);
    const map = this._scene.sessionEntity.c.map;
    const collisionTracker = getCollisionTracker();
    const area = new Vector2();
    const targets: Vector2[] = [];

    for (area.x = from.x; area.x < to.x; ++area.x) {
      for (area.y = from.y; area.y < to.y; ++area.y) {
        // Skip if out of map bounds
        if (area.x < 0 || area.x >= map.columns) continue;
        if (area.y < 0 || area.y >= map.rows) continue;
        targets.push(area.clone());
      }
    }

    let cur = 0;
    const props = { radius: 0 };

    this._animateWave();

    await gsap.to(props, {
      duration,
      radius,
      ease: `steps(${radius})`,
      onUpdate: () => {
        if (cur === props.radius) return;
        cur = props.radius;
        targets
          .filter((target) => {
            const dist = this._options.squareOff
              ? Math.max(
                  Math.abs(target.x - position.x),
                  Math.abs(target.y - position.y),
                )
              : Math.round(target.distanceTo(position));
            return dist <= cur;
          })
          .forEach((target) => {
            const collided = collisionTracker.collideAt(
              map,
              target,
              'attack',
              collider,
            );
            if (collided) {
              this.targetCount++;

              // Remove targets already collided from the targets list
              targets.splice(targets.indexOf(target), 1);
            }
          });
      },
    });
  }

  private async _animateWave() {
    const { texture, radius, duration } = this._options;
    const { x, y } = mapToViewPosition(this._options.position, true);
    const wave = Sprite.from(texture ?? 'fx.bomb/fx.bomb.wave.png');
    const size =
      (this._options.squareOff ? Math.SQRT2 * radius : radius) *
      2 *
      config.tile.size;
    const baseSize = Math.max(wave.width, wave.height);
    const targetScale = size / baseSize;
    const fadeDuration = duration * 0.2;

    wave.anchorX = 0.5;
    wave.anchorY = 0.5;
    wave.scaleX = 0;
    wave.scaleY = 0;
    wave.x = x;
    wave.y = y;

    this._scene.stage.addChild(wave);

    await gsap
      .timeline()
      .to(wave, {
        duration,
        scaleX: targetScale,
        scaleY: targetScale,
        ease: 'none',
      })
      .to(
        wave,
        {
          duration: fadeDuration,
          alpha: 0,
          ease: 'quad.out',
        },
        duration - fadeDuration,
      );

    this._scene.stage.removeChild(wave);
  }
}
